import { captureException } from 'marketing-site/lib/sentry'
import { IFormField } from '../@types/FormField'
import { FieldType } from '../@types/FormField'
import { IProps as IFormFieldNumber } from '../../FormFieldNumber'
import { IsBusinessEmailValidator } from './validator/IsBusinessEmail.validator'
import { IsEmailValidator, WRONG_EMAIL_ERROR } from './validator/IsEmail.validator'
import { IsMinLengthValidator } from './validator/IsMinLength.validator'
import { IsStringValidator } from './validator/IsString.validator'
import { IsValidNumberValidator } from './validator/IsValidNumber.validator'
import { MaxNumberValidator } from './validator/MaxNumber.validator'
import { MinNumberValidator } from './validator/MinNumber.validator'
import { RequiredValidator } from './validator/Required.validator'
import {
  IValidationOptions,
  ValidationFunction,
  ValidatorValue,
} from './validator/Validator.interface'

// Other declarative methods failed to work with async functions or were less readable (reduce)
const runValidators = async (
  validators: ValidationFunction[],
  value: ValidatorValue,
): Promise<string> => {
  for (let i = 0; i < validators.length; i++) {
    const validationError = await validators[i](value)
    if (validationError) {
      return validationError
    }
  }
  return ''
}

export default async function getValidationErrorIfAny(
  field: IFormField,
  value: string | number | boolean | undefined,
  options: IValidationOptions = {},
): Promise<string> {
  const fieldValidators: ValidationFunction[] | undefined = getFieldValidator(field, options)

  return fieldValidators ? await runValidators(fieldValidators, value) : ''
}

export async function getValidationErrorWithCallback(
  field: IFormField,
  value: string | number | boolean | undefined,
  validationCallback: (isValidationSuccessful: boolean) => void,
  options: IValidationOptions = {},
): Promise<string> {
  const errorMessage = await getValidationErrorIfAny(field, value, options)
  const isValidationSuccessful = !errorMessage
  validationCallback(isValidationSuccessful)
  return errorMessage ?? ''
}

const validateIf = (validator: ValidationFunction, booleanOption?: boolean) => {
  if (!!booleanOption) {
    return validator
  }

  return () => ''
}

const PHONE_MIN_LENGTH = 8
const PHONE_ERROR_TEMPLATE = () => 'Please provide a valid phone number'

function getFieldValidator(field: IFormField, options: IValidationOptions) {
  if (field.type === 'hidden') return

  const DEFAULT_VALIDATORS = [validateIf(RequiredValidator(), field.required)]

  const validationMap: Record<FieldType, ValidationFunction[]> = {
    [FieldType.EMAIL]: [
      validateIf(RequiredValidator(), field.required),
      IsStringValidator(WRONG_EMAIL_ERROR),
      IsEmailValidator(),
      validateIf(IsBusinessEmailValidator(), options.forceBusinessEmail),
    ],
    [FieldType.CHECKBOX]: [validateIf(RequiredValidator(), field.required)],
    [FieldType.RADIO_BUTTONS]: [validateIf(RequiredValidator(), field.required)],
    [FieldType.SELECT]: [validateIf(RequiredValidator(), field.required)],
    [FieldType.TEXTAREA]: [validateIf(RequiredValidator(), field.required)],
    [FieldType.TEXT]: [validateIf(RequiredValidator(), field.required)],
    [FieldType.PHONE]: [
      validateIf(RequiredValidator(), field.required),
      IsMinLengthValidator(PHONE_MIN_LENGTH, PHONE_ERROR_TEMPLATE),
    ],
    [FieldType.HTML_TEXT]: [],
    [FieldType.HIDDEN]: [],
    [FieldType.NUMBER]: [
      validateIf(RequiredValidator(), field.required),
      IsValidNumberValidator(),
      MinNumberValidator((field as IFormFieldNumber).min),
      MaxNumberValidator((field as IFormFieldNumber).max),
    ],
  }

  const validators = validationMap[field.type]

  if (!validators) {
    captureException(new Error(`Tried to validate an unsupported field with type=${field.type}`))
  }

  return validators ?? DEFAULT_VALIDATORS
}
